import { AxiosError } from 'axios'

import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import APIServices from '../../api/APIServices'

// Create a unique, descriptive key for this query
const CUSTOMER_CATEGORY_QUERY_KEY = 'customer-category'

type CustomerCategory = {
  id: number
  name: string
}

type CustomerCategoriesResponse = CustomerCategory[]

/**
 * Fetch customer category from the API
 * Separating API call logic from the hook for better testability
 */
const fetchCustomerCategories =
  async (): Promise<CustomerCategoriesResponse> => {
    const response = await APIServices.get('/invoice/customers/categories')
    return response.data
  }

/**
 * Custom hook for fetching customer category
 *
 * @param options - TanStack Query options for customizing query behavior
 * @returns Query result object containing data, loading states, and utility functions
 */
export const useCustomerCategoriesQuery = ({
  businessId,
  options,
}: {
  businessId: number
  options?: Partial<UseQueryOptions<CustomerCategoriesResponse, AxiosError>>
}) => {
  // Query hook
  const queryResult = useQuery<CustomerCategoriesResponse, AxiosError>({
    // Create a structured query key that includes all parameters
    queryKey: [CUSTOMER_CATEGORY_QUERY_KEY, businessId],

    // Separate the API call logic from the query configuration
    queryFn: () => fetchCustomerCategories(),

    // Default options that can be overridden
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000, // 10 minutes (location data doesn't change often)

    // Apply any custom options
    ...options,
  })

  return {
    customerCategories: queryResult.data || [],
    isLoadingCustomerCategories: queryResult.isLoading,
    isErrorCustomerCategories: queryResult.isError,
    errorCustomerCategories: queryResult.error,
    refetchCustomerCategories: queryResult.refetch,
    isFetchingCustomerCategories: queryResult.isFetching,
  }
}

/**
 * Helper function to get the customer categories query key
 * Useful for cache invalidation or prefetching
 */
export const getCustomerCategoriesQueryKey = (businessId: number) => [
  CUSTOMER_CATEGORY_QUERY_KEY,
  businessId,
]
